/**
 * Phase 3 — PromoteConfigurationModal
 *
 * Opens from CandidateTable rows or the BaselineVsBestCard primary
 * button. Submits to /api/optimisation/studies/{id}/promote and
 * navigates to the new scenario's Workbench page on success.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import optimisation from "@/api/optimisation";

export default function PromoteConfigurationModal({
    open,
    onOpenChange,
    studyId,
    studyName,
    candidateId,
}) {
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [blocking, setBlocking] = useState(null);

    useEffect(() => {
        if (open) {
            setName(`ROE promoted: ${studyName || "study"}`);
            setDescription("");
            setBlocking(null);
        }
    }, [open, studyName]);

    const submit = async () => {
        if (!candidateId) return;
        setSubmitting(true);
        setBlocking(null);
        try {
            const res = await optimisation.promoteCandidate(studyId, {
                candidate_id: candidateId,
                target: { kind: "scenario", name, description },
            });
            toast.success("Configuration promoted to a new scenario.");
            onOpenChange(false);
            navigate(res.workbench_path);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 && detail?.error === "blocking_validation") {
                setBlocking(detail.validation_result || {});
            } else {
                toast.error(
                    (typeof detail === "string" && detail) ||
                        detail?.message ||
                        e?.message ||
                        "Failed to promote candidate",
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-ux-bg-1 border-ux-border-1 max-w-[520px]"
                data-testid="promote-modal"
            >
                <DialogHeader>
                    <DialogTitle>Promote configuration</DialogTitle>
                    <DialogDescription className="text-[12px] text-ux-text-3">
                        Saves the candidate as a new Workbench scenario you can
                        edit and run. Provenance is stamped on the new scenario.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label htmlFor="promote-name">Scenario name</Label>
                        <Input
                            id="promote-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="promote-name-input"
                            className="bg-ux-bg-2"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="promote-description">
                            Description (optional)
                        </Label>
                        <Textarea
                            id="promote-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            data-testid="promote-description-input"
                            className="bg-ux-bg-2 text-[12px]"
                            placeholder="Brief note on what this configuration is for…"
                        />
                    </div>

                    <div className="space-y-1">
                        <Label>Promote as</Label>
                        <RadioGroup
                            value="scenario"
                            className="space-y-1"
                            onValueChange={() => {}}
                        >
                            <label
                                className="flex items-start gap-3 rounded p-2 border border-ux-border-1 bg-ux-bg-2/40 cursor-pointer"
                                data-testid="promote-target-scenario"
                            >
                                <RadioGroupItem value="scenario" id="promote-target-scenario" />
                                <span className="space-y-0.5">
                                    <span className="block text-[12px] text-ux-text-1 font-medium">
                                        Scenario (Workbench)
                                    </span>
                                    <span className="block text-[11px] text-ux-text-3">
                                        Lands as a draft scenario you can open, edit and
                                        run via the Workbench.
                                    </span>
                                </span>
                            </label>
                            <label
                                className="flex items-start gap-3 rounded p-2 border border-ux-border-1 bg-ux-bg-2/20 cursor-not-allowed opacity-60"
                                aria-disabled
                            >
                                <RadioGroupItem value="parameter_set" disabled />
                                <span className="space-y-0.5">
                                    <span className="block text-[12px] text-ux-text-2">
                                        Parameter set
                                    </span>
                                    <span className="block text-[11px] text-ux-text-3">
                                        Promote a candidate's deltas as a
                                        reusable parameter set.
                                    </span>
                                </span>
                            </label>
                            <label
                                className="flex items-start gap-3 rounded p-2 border border-ux-border-1 bg-ux-bg-2/20 cursor-not-allowed opacity-60"
                                aria-disabled
                            >
                                <RadioGroupItem value="client_request" disabled />
                                <span className="space-y-0.5">
                                    <span className="block text-[12px] text-ux-text-2">
                                        Client request (YAML)
                                    </span>
                                    <span className="block text-[11px] text-ux-text-3">
                                        Export the recommended configuration
                                        as a YAML run request.
                                    </span>
                                </span>
                            </label>
                        </RadioGroup>
                    </div>

                    {blocking && (
                        <div
                            className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/30 p-3 space-y-1"
                            data-testid="promote-blocking"
                        >
                            <div className="flex items-center gap-2 text-[12px] text-ux-danger">
                                <AlertCircle className="w-3.5 h-3.5" />
                                <span>Blocked by validation</span>
                            </div>
                            <ul className="text-[11px] text-ux-text-2 space-y-0.5">
                                {(blocking.blocking || []).map((b, idx) => (
                                    <li key={idx}>
                                        <code className="font-mono">{b.rule_id}</code> —{" "}
                                        {b.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || !name.trim()}
                        data-testid="promote-submit"
                    >
                        {submitting ? "Promoting…" : "Promote configuration"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
